import { useEffect, useRef, useState } from "react";
import HolifyaLogoComplete from "../../../components/HolifyaLogoComplete";
import HolifyaLogoIcon from "../../../components/HolifyaLogoIcon";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import VersionIndicator from "../../../components/VersionIndicator";
import VersionPopup from "../../../components/VersionPopup";
import { useVersionTrigger } from "../../../hooks/useVersionTrigger";
import { Appointment } from "../../../pages/platform/components/Appointment";
import { NavBarElement } from "../../../pages/platform/components/nav-bar-element.enum";
import SessionExpiredDialog from "../../../pages/platform/components/SessionExpiryDialog";
import { getAppointmentsByOrganizerEmail } from "../../../utils/apis/booking-service";
import { getPatientsApi } from "../../../utils/apis/medical-user-service";
import { useAppointments } from "../../../utils/context/AppointmentsContext";
import { useAuth } from "../../../utils/context/AuthContext";
import { usePatients } from "../../../utils/context/PatientsContext";
import { useUserData } from "../../../utils/context/UserDataContext";
import { AppointmentStatus } from "../../../utils/Enums";
import { checkForceUpdate } from "../../../utils/forceUpdateDetection";
import { handleDaysDifference, handleHoursDifference, handleMinutesDifference } from "../../../utils/Functions";
import { startUpdateDetection, stopUpdateDetection } from "../../../utils/updateDetection";
import NavBar from "../../navbar/components/NavBar";

interface LayoutProps {
  currentItem: NavBarElement;
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({
  currentItem,
  children,
}) => {
  const { auth } = useAuth()
  const { userData } = useUserData()
  const { setPatients } = usePatients()
  const {
    setAppointments,
    setActiveAppointments,
    setFutureActiveAppointments,
    setOldActiveAppointments,
  } = useAppointments()

  const idToken = auth.idToken
  const userId = auth.userId
  const role = auth.role
  const email = userData.email

  const [shouldShowExpirySessionDialog, setShouldShowExpirySessionDialog] = useState(false)
  const [isAuthLoading, setIsAuthLoading] = useState(true)
  const [isVersionPopupOpen, setIsVersionPopupOpen] = useState(false)

  const contentRef = useRef<HTMLDivElement>(null);

  // Version popup trigger
  const versionTrigger = useVersionTrigger({
    onTrigger: () => setIsVersionPopupOpen(true)
  });

  // Update detection
  useEffect(() => {
    // Start update detection when component mounts
    startUpdateDetection({
      onUpdateAvailable: (result) => {
        console.log('Update available:', result);
        // You can show a notification here
      },
      onForceUpdate: (result) => {
        console.log('Force update required:', result);
        // Handle force update - could show a modal blocking the app
      },
      onError: (error) => {
        console.error('Update detection error:', error);
      }
    });

    // Check for force update
    checkForceUpdate({
      onForceUpdate: (state) => {
        console.log('App blocked by force update:', state);
        // Handle force update blocking
      }
    });

    // Cleanup on unmount
    return () => {
      stopUpdateDetection();
    };
  }, []);

  useEffect(() => {
    const fetchSessionAndAppointments = async () => {
      if (!idToken || !userId || !email || !role) return;

      try {
        const patients = await getPatientsApi(idToken, userId, role, handleSessionExpiry);
        if (patients) {
          setPatients(patients)
        }

        const appointments = await getAppointmentsByOrganizerEmail(idToken, email, role, handleSessionExpiry);
        if (appointments) {
          const allAppointments: Appointment[] = appointments.map((appointment) => ({
            date: appointment.payload.scheduled_event.start_time,
            patientName: appointment.payload.name,
            patientEmail: appointment.email,
            editUrl: appointment.payload.reschedule_url,
            connectUrl: appointment.payload.scheduled_event.location.join_url,
            cancelUrl: appointment.payload.cancel_url,
            remainingDays: handleDaysDifference(appointment.payload.scheduled_event.start_time),
            remainingHours: handleHoursDifference(appointment.payload.scheduled_event.start_time),
            remainingMinutes: handleMinutesDifference(appointment.payload.scheduled_event.start_time),
            status: appointment.payload.status,
            expert_email: appointment.organizer_email,
          }));

          const allActiveAppointments = allAppointments.filter(
            (app) => app.status === AppointmentStatus.Active
          );
          const allFutureActiveAppointments = allActiveAppointments.filter(
            (app) => app.remainingMinutes > -30
          );
          const allOldActiveAppointments = allActiveAppointments.filter(
            (app) => app.remainingMinutes <= -30
          );

          setAppointments(allAppointments);
          setActiveAppointments(allActiveAppointments);
          setFutureActiveAppointments(allFutureActiveAppointments);
          setOldActiveAppointments(allOldActiveAppointments);
        }
      } catch (error) {
        console.error("Unexpected error occurred:", error);
      } finally {
        setIsAuthLoading(false);
      }
    };

    const handleSessionExpiry = () => {
      console.warn("Session expired. Redirecting to login...");
      setShouldShowExpirySessionDialog(true);
    };

    fetchSessionAndAppointments();

    const intervalId = setInterval(fetchSessionAndAppointments, 5 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [idToken, userId, email]);

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [children]);

  // ********************************************************************************

  return (
    <div className="w-dvh h-dvh flex flex-col md:flex-row-reverse overflow-hidden">
      <div className="flex-1 h-full flex flex-col min-h-0">
        {/* Fixed header with logo */}
        <div className="flex-shrink-0 p-4 md:p-6 border-b border-gray-200 bg-white">
          {/* Mobile: Icon logo */}
          <div className="block md:hidden">
            <HolifyaLogoIcon />
          </div>
          {/* Desktop: Full logo */}
          <div className="hidden md:block">
            <HolifyaLogoComplete />
          </div>
        </div>
        
        {/* Scrollable content area */}
        <div className="flex-1 w-full overflow-y-auto scrollbar-hide px-4 md:px-6 pt-4 md:pt-6 min-h-0 max-h-full">
          <div ref={contentRef} className="w-full">
            {children}
          </div>
        </div>
      </div>
      <NavBar currentItem={currentItem} />
      {
        shouldShowExpirySessionDialog && <SessionExpiredDialog />
      }
      {
        isAuthLoading && <LoadingSpinner />
      }
      {/* Version Indicator (Development Mode) */}
      <VersionIndicator
        onClick={() => setIsVersionPopupOpen(true)}
      />
      {/* Version Popup */}
      <VersionPopup
        isOpen={isVersionPopupOpen}
        onClose={() => setIsVersionPopupOpen(false)}
      />
    </div>
  )
}

export default Layout
